import { ensureProtoProps, eq, safeEq } from '@rehaje/shared'

import { FatalDecoratorLimitGetterError } from '../errors'
import { COMPUTED } from '../flags'
import { Computed as SignalComputed } from '../signal'
import { action } from './action'

import type {
  ComputedAnnotation,
  ComputedOptions,
  ReactivePrototype
} from '../types'

class Computed extends SignalComputed {
  __rr_flags = COMPUTED
  _parent: any

  constructor(
    compute: () => any,
    options: ComputedOptions = {},
    parent?: any,
    key?: string
  ) {
    super(compute, key)
    this._eq = safeEq(options.equals ?? eq)
    this._parent = parent ?? this
    this._key ??= 'value'
  }
}

function createDescriptor(
  proto: any,
  key: string,
  getter: () => any,
  setter?: (value: any) => void,
  options?: ComputedOptions
): PropertyDescriptor
function createDescriptor(
  proto: ReactivePrototype | undefined,
  key: string,
  getter: () => any,
  setter?: (value: any) => void,
  options?: ComputedOptions
): PropertyDescriptor {
  if (proto !== undefined) {
    const computedKeys = ensureProtoProps(proto, '__rr_computed_keys', [])
    if (!computedKeys.includes(key)) computedKeys.push(key)
  }

  return {
    configurable: false,
    enumerable: true,
    get: new Function(
      'Computed',
      'getter',
      'options',
      `
      return function () {
        this.__rr_computed_${key} ??= new Computed(getter.bind(this), options, this, "${key}")
        return this.__rr_computed_${key}.value
      }
      `
    )(Computed, getter, options),
    set: new Function(
      'action',
      'setter',
      `
      return function (value) {
        if (typeof setter !== 'function') return
        this.__rr_methods_${key} ??= action.bound(setter.bind(this))
        this.__rr_methods_${key}.call(this, value)
      }
      `
    )(action, setter)
  }
}

export const computed: ComputedAnnotation<ComputedOptions> = (
  value,
  options
) => {
  return new Computed(value, options)
}

computed._name = 'computed'

computed._tsDecorator = (proto, key, descriptor, options) => {
  if (typeof descriptor?.get !== 'function') {
    throw new FatalDecoratorLimitGetterError(computed._name, key)
  }
  return createDescriptor(proto, key, descriptor.get, descriptor.set, options)
}

computed._esGetterDecorator = (getter, context, options) => {
  const descriptor = createDescriptor(
    undefined,
    String(context.name),
    getter,
    undefined,
    options
  )
  if (typeof descriptor?.get !== 'function') {
    throw new FatalDecoratorLimitGetterError(computed._name, context.name)
  }
  return descriptor.get
}
